import { useBottomSheet } from '../../../../../../state-management/contexts/BottomSheetContext'
import { useToast } from '../../../../../../state-management/contexts/ToastContext'
import {
  formatSipIncreasePlan,
  type PlanAmount
} from '../../../display-values/sipPlanAmount'
import type { SipIncreaseRecommendation } from '../../../sip/api/sipPlan.types'
import { postFilledSipPlanCta } from '../../fundExecutionHelpers'
import { useEnsureSipExecutionAllowed } from '../useEnsureSipExecutionAllowed'
import {
  FolioSourceId,
  SipFolioActionTone,
  type SipFolioRow,
  sumFolioSipAmounts
} from '../folio'
import { SipFolioRowList, SipModifySheetLayout } from '../ModifySheet'
import { EventNames } from '../../../../../../types/misc.types'
import { trackEvent } from '../../../../../../utils/appBridge'
import { PlansExecutionEventIds, PlansScreen } from '../../../analytics/plansEvents'

export function useIncreaseSipAction(options?: { isRecommendationSheetSource?: boolean }) {
  const { showToast } = useToast()
  const {
    openBottomSheet,
    closeBottomSheet,
    openNestedBottomSheet,
    closeNestedBottomSheet
  } = useBottomSheet()
  const openSheet = options?.isRecommendationSheetSource ? openNestedBottomSheet : openBottomSheet
  const closeSheet = options?.isRecommendationSheetSource ? closeNestedBottomSheet : closeBottomSheet
  const ensureSipExecutionAllowed = useEnsureSipExecutionAllowed(options)

  async function onIncreaseSip(recommendation: SipIncreaseRecommendation, initiateSipCta: unknown) {
    const fundName = recommendation?.fundName ?? ''
    const isin = recommendation?.isin ?? ''
    trackEvent({
      eventName: EventNames.ButtonTap,
      payload: {
        ScreenName: PlansScreen.SipYourPlanRecommendation,
        eventId: PlansExecutionEventIds.SipIncrease,
        buttonLabel: 'Increase SIP',
        ...(fundName ? { fundName } : {}),
        ...(isin ? { fundIsin: isin } : {})
      }
    })
    const isAllowed = await ensureSipExecutionAllowed(isin, fundName)
    if (!isAllowed) return

    const { formattedTargetSip, formattedIncreaseAmount } = formatSipIncreasePlan({
      currentTotalSip: sumFolioSipAmounts(recommendation),
      rawTargetSip: recommendation?.recommendedSipAmount ?? 0
    })

    openSheet(
      <IncreaseSipModifyBottomSheet
        onDismiss={closeSheet}
        recommendation={recommendation}
        formattedTargetSip={formattedTargetSip}
        formattedIncreaseAmount={formattedIncreaseAmount}
        onPowerUpClick={() => {
          closeSheet()
          postFilledSipPlanCta(
            initiateSipCta,
            {
              isin,
              amount: String(formattedIncreaseAmount.value)
            },
            showToast
          )
        }}
      />,
      closeSheet
    )
  }

  return { onIncreaseSip }
}

export function IncreaseSipModifyBottomSheet({
  onDismiss,
  recommendation,
  formattedTargetSip,
  formattedIncreaseAmount,
  onPowerUpClick
}: {
  onDismiss: () => void
  recommendation: SipIncreaseRecommendation
  formattedTargetSip: PlanAmount
  formattedIncreaseAmount: PlanAmount
  onPowerUpClick?: () => void
}) {
  return (
    <SipModifySheetLayout
      onDismiss={onDismiss}
      mfLogoUrl={recommendation?.mfLogoUrl ?? ''}
      fundName={recommendation?.fundName ?? ''}
      fundIsin={recommendation?.isin ?? ''}
      heading={`Increase total SIP to ${formattedTargetSip.label}/month`}
    >
      <SipFolioRowList
        rows={buildIncreaseFolioRows(recommendation, formattedIncreaseAmount)}
        fundName={recommendation?.fundName ?? ''}
        fundIsin={recommendation?.isin ?? ''}
        onPowerUpClick={onPowerUpClick}
      />
    </SipModifySheetLayout>
  )
}

/**
 * Product display order: PowerUp → External → Infinite.
 * Only PowerUp can close the gap (start / top-up CTA). External + Infinite are read-only.
 */
function buildIncreaseFolioRows(
  recommendation: SipIncreaseRecommendation,
  formattedIncreaseAmount: PlanAmount
): SipFolioRow[] {
  const rows: SipFolioRow[] = []
  const powerupSipAmount = recommendation?.powerupSipAmount ?? 0
  const externalSipAmount = recommendation?.externalSipAmount ?? 0
  const infiniteSipAmount = recommendation?.infiniteSipAmount ?? 0

  const powerUpRow = buildPowerUpIncreaseRow(powerupSipAmount, formattedIncreaseAmount)
  if (powerUpRow != null) {
    rows.push(powerUpRow)
  }

  if (externalSipAmount > 0) {
    rows.push(buildReadOnlyFolioRow(FolioSourceId.EXTERNAL, externalSipAmount))
  }

  if (infiniteSipAmount > 0) {
    rows.push(buildReadOnlyFolioRow(FolioSourceId.INFINITE, infiniteSipAmount))
  }

  return rows
}

/** PowerUp row — omitted when there is nothing to show or do. */
function buildPowerUpIncreaseRow(
  powerupSipAmount: number,
  formattedIncreaseAmount: PlanAmount
): SipFolioRow | null {
  if (powerupSipAmount <= 0 && formattedIncreaseAmount.value <= 0) {
    return null
  }

  if (powerupSipAmount <= 0) {
    return {
      id: FolioSourceId.POWERUP,
      currentAmount: 0,
      actionText: `Start an SIP of ${formattedIncreaseAmount.label}/mo`,
      actionTone: SipFolioActionTone.SUCCESS,
      helperText: null,
      interactive: true
    }
  }

  if (formattedIncreaseAmount.value <= 0) {
    return {
      id: FolioSourceId.POWERUP,
      currentAmount: powerupSipAmount,
      actionText: null,
      actionTone: SipFolioActionTone.NEUTRAL,
      helperText: null,
      interactive: false
    }
  }

  return {
    id: FolioSourceId.POWERUP,
    currentAmount: powerupSipAmount,
    actionText: `Start a new SIP of ${formattedIncreaseAmount.label}/mo`,
    actionTone: SipFolioActionTone.SUCCESS,
    helperText: null,
    interactive: true
  }
}

function buildReadOnlyFolioRow(sourceId: FolioSourceId, currentAmount: number): SipFolioRow {
  return {
    id: sourceId,
    currentAmount,
    actionText: null,
    actionTone: SipFolioActionTone.NEUTRAL,
    helperText: null,
    interactive: false
  }
}
